import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import Alert from "react-bootstrap/Alert";
import { Helmet } from "react-helmet-async";
import { withRouter } from "react-router-dom";
import AdminRoleCreateForm from "../admin-role-create-form/admin-role-create-form";
import CancelModal from "../../../../components/cancel-modal/cancel-modal";
import ConfirmationModal from "../../../../components/confirmation-modal/confirmation-modal";
import {
  GetAllDepartmentsAPI,
  GetDepartmentAPI,
} from "../../../../services/api/department-api";
import {
  CreateBlankRoleNew,
  CreateRoleNew,
  GetEditRoleNew,
  EditRoleNew,
  RoleExistsWithNameNew,
} from "../../../../services/api/role-api";
import { LoginContext } from "../../../../services/api/authentication-api";
import "./admin-role-create-panel-new.scss";

const AdminRoleCreatePanelNew = ({ location, history }) => {
  const [isEdit] = useState(location.pathname.split("/")[3] === "edit");
  const [role, setRole] = useState({});
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [showAlert, setShowAlert] = useState(false);
  const [showCancelPanel, setShowCancelPanel] = useState(false);
  const [showConfirmPanel, setShowConfirmPanel] = useState(false);
  const [alertAction, setAlertAction] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [alertVariant, setAlertVariant] = useState("danger");
  const [loaded, setLoaded] = useState(false);
  const user = useContext(LoginContext);

  useEffect(() => {
    const fetchInitialData = async () => {
      let searchQuery = {
        userId: user.userID,
        pageSize: 100000,
        pageNumber: 1,
        sortDesc: false,
        sortBy: "name",
        filters: [],
      };

      const departmentResult = await GetAllDepartmentsAPI(searchQuery);
      setDepartments(departmentResult.data.dataList);

      if (!isEdit) {
        try {
          const result = await CreateBlankRoleNew();
          result.Company = -1;
          setRole(result);
          setLoaded(true);
        } catch (error) {
          setShowAlert(true);
          setAlertAction("Cannot get blank role.");
          setAlertMessage(error.toString());
          setAlertVariant("danger");
        }
      } else {
        try {
          let roleID = location.pathname.split("/")[4];
          const role = await GetEditRoleNew(roleID);
          const res = await GetDepartmentAPI(role.Company);
          const department = res?.data;
          setRole(role);
          setSelectedDepartment({
            id: department?.Id ?? 0,
            name: department?.Name ?? "",
          });
          setLoaded(true);
        } catch (error) {
          setShowAlert(true);
          setAlertAction("Cannot get wanted role.");
          setAlertMessage(error.toString());
          setAlertVariant("danger");
        }
      }
    };

    fetchInitialData();
  }, []);

  const saveRole = (newRole, actions) => {
    RoleExistsWithNameNew(newRole.Name, newRole.Company).then((result) => {
      if (result != null) {
        if (result.Id !== newRole.Id) {
          setShowAlert(true);
          setAlertAction("A role with that name in the department exists.");
          setAlertMessage("");
          setAlertVariant("danger");
          actions.setSubmitting(false);
          return;
        } else {
          updateRole(newRole, actions);
        }
      } else {
        updateRole(newRole, actions);
      }
      actions.setSubmitting(false);
    });
  };

  const updateRole = async (newRole, actions) => {
    newRole.Created = new Date();

    if (!isEdit) {
      try {
        await CreateRoleNew(newRole);
        setShowConfirmPanel(true);
        actions.setSubmitting(false);
      } catch (error) {
        setShowAlert(true);
        setAlertAction("Cannot create new role.");
        setAlertMessage(error.toString());
        setAlertVariant("danger");
        actions.setSubmitting(false);
      }
    } else {
      try {
        await EditRoleNew(newRole);
        setShowConfirmPanel(true);
        actions.setSubmitting(false);
      } catch (error) {
        setShowAlert(true);
        setAlertAction("Cannot update current role.");
        setAlertMessage(error.toString());
        setAlertVariant("danger");
        actions.setSubmitting(false);
      }
    }
  };

  const displayAlert = () => {
    window.setTimeout(() => {
      setShowAlert(false);
    }, 5000);

    return (
      <Alert
        show={showAlert}
        onClose={() => setShowAlert(false)}
        variant={alertVariant}
        dismissible
      >
        <Alert.Heading>{alertAction}</Alert.Heading>
        <p>{alertMessage}</p>
      </Alert>
    );
  };

  return (
    <div className="role-edit-panel">
      <Helmet>
        <meta charSet="utf-8" />
        <title>
          {isEdit ? "TRIYO Edit Role · " + role.Name : "TRIYO · Create Role"}
        </title>
      </Helmet>
      {showAlert ? displayAlert() : <></>}
      {showCancelPanel ? (
        <CancelModal
          show={showCancelPanel}
          onHide={() => {
            setShowCancelPanel(false);
          }}
          redirectURL={"/admin?tab=roles"}
        />
      ) : (
        <></>
      )}
      {showConfirmPanel && (
        <ConfirmationModal
          show={showConfirmPanel}
          onHide={() => {
            history.push("/admin?tab=roles");
          }}
          redirectURL={"/admin?tab=roles"}
          header={!isEdit && "Create Role Confirmation"} // message defaults to "Save Confirmation"
          confirmationText={!isEdit && "Role has been created successfully."} // message defaults to "Your changes have been saved"
        />
      )}
      <h4 className="panel-title">{isEdit ? "Edit Role" : "Create Role"} </h4>
      <AdminRoleCreateForm
        loaded={loaded}
        role={role}
        isEdit={isEdit}
        departments={departments}
        selectedDepartment={selectedDepartment}
        setSelectedDepartment={(department) =>
          setSelectedDepartment(department)
        }
        saveRole={(newRole, actions) => saveRole(newRole, actions)}
        onCancel={() => setShowCancelPanel(true)}
      />
    </div>
  );
};

AdminRoleCreatePanelNew.propTypes = {
  location: PropTypes.object.isRequired,
  history: PropTypes.array.isRequired,
};
AdminRoleCreatePanelNew.contextType = LoginContext;
export default withRouter(AdminRoleCreatePanelNew);
